import { reactive, ref, Ref, getCurrentInstance, onMounted } from 'vue';
import { renderSlotName, resolveComponent } from '@/views/components/chart/utils/resolver';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';

// Constants
import { MODULES, EVENTS } from '@/constants/modules/activity-log/activity-status';

import { MODE_KEYS, type ModeKey } from '@/types/modules/core/frequency';

interface Props {
  isOpen: boolean;
  chartName: string;
  description: string;
  filters: Record<string, any>[];
  dataInfo: DataInfoModal.DataInfoTable;
  chartDataInfoExportProps: Charts.ChartDataInfoExportProps;
}

export default function useDataInfoModal(props: Props) {
  const vm = getCurrentInstance()?.proxy;

  const loadingExport: Ref<boolean> = ref(false);

  const { t, locale } = useI18n();

  const chartDataInfoExportProps: Ref<Charts.ChartDataInfoExportProps> = ref({
    chartLayoutId: props.chartDataInfoExportProps.chartLayoutId,
    chartId: props.chartDataInfoExportProps.chartId,
    chartSetId: props.chartDataInfoExportProps.chartSetId,
    implType: props.chartDataInfoExportProps.implType,
    implKeyRef: props.chartDataInfoExportProps.implKeyRef,
    filters: props.chartDataInfoExportProps.filters,
  });

  const columns: Ref<BaseTable.TableColumn[]> = ref([]);
  const data: Ref<Record<string, any>[]> = ref([]);
  const pagination: BaseTable.Pagination = reactive({
    currentPage: 1,
    perPage: 5,
    totalRows: 0,
  });

  const onCloseModal = () => {
    vm?.$emit('close-modal');
  };

  const resolveInfoFilterKey = (key: string) => {
    if (isModeKey(key)) {
      return t('bi.frequency');
    }
    switch (key) {
      case 'startDate':
        return t('bi.startDate');
      case 'endDate':
        return t('bi.endDate');
      case 'source':
        return t('bi.source');
      default:
        return key;
    }
  };

  const formatFrequencyValue = (mode: ModeKey, rawValue: string) => {
    const stepNumber = (input: string, fallback = 1) => {
      const parsed = Number.parseInt(input, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
    };
    const everyLabel = t('datepicker.frequency.every') as string;
    const every = (unitKey: string, singleLabel: string, step: number) =>
      step > 1 ? `${everyLabel} ${step} ${t(unitKey)}` : singleLabel;

    switch (mode) {
      case 'Default':
        return t('datepicker.frequency.defaultTab') as string;
      case 'Daily':
        return t('datepicker.freq.daily') as string;
      case 'EveryDays':
        return every('datepicker.units.day', t('datepicker.freq.daily') as string, stepNumber(rawValue, 1));
      case 'Weekly':
        return t('datepicker.freq.weekly') as string;
      case 'EveryWeeks':
        return every('datepicker.units.week', t('datepicker.freq.weekly') as string, stepNumber(rawValue, 1));
      case 'Monthly':
        return t('datepicker.freq.monthly') as string;
      case 'EveryMonths': {
        let step = stepNumber(rawValue, 1);
        let day = 1;
        try {
          const parsed = JSON.parse(rawValue) as { Step?: number; DayOfMonth?: number };
          step = stepNumber(String(parsed.Step ?? step), step);
          day = Math.min(31, Math.max(1, Number(parsed.DayOfMonth ?? day)));
        } catch {
          day = 1;
        }
        const base = every('datepicker.units.month', t('datepicker.freq.monthly') as string, step);
        return `${base} (${t('datepicker.frequency.onDayOfMonthPrefix')} ${day} ${t('datepicker.frequency.onDayOfMonthSuffix')})`;
      }
      case 'Quarterly':
        return t('datepicker.freq.quarterly') as string;
      case 'EveryQuarters':
        return every('datepicker.units.quarter', t('datepicker.freq.quarterly') as string, stepNumber(rawValue, 1));
      case 'Yearly':
        return t('datepicker.freq.yearly') as string;
      case 'EveryYears':
        return every('datepicker.units.year', t('datepicker.freq.yearly') as string, stepNumber(rawValue, 1));
      default:
        return rawValue;
    }
  };

  const resolveInfoFilterValue = (value: string | string[], key?: string) => {
    if (Array.isArray(value)) return value;
    if (key && isModeKey(key)) {
      return formatFrequencyValue(key, value);
    }
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) {
      return value;
    }

    const baseLocale = String(locale.value || '');
    const gregorianLocale = baseLocale.toLowerCase().startsWith('th') ? `${baseLocale}-u-ca-gregory-nu-latn` : baseLocale;

    return DateTime.fromJSDate(d).setLocale(gregorianLocale).toLocaleString(DateTime.DATE_MED);
  };

  function isModeKey(key: string): key is ModeKey {
    return key === 'Default' || (MODE_KEYS as readonly string[]).includes(key);
  }

  const onClickExportDataInfo = () => {
    const filterMap = chartDataInfoExportProps.value.filters.reduce(
      (acc, el) => {
        if (isModeKey(el.key)) {
          // เงื่อนไขสำหรับ ModeKey → เก็บแค่ mode ล่าสุด
          acc.mode = { key: el.key, value: el.value };
        } else {
          // คีย์อื่นเก็บปกติ
          acc[el.key] = el.value;
        }
        return acc;
      },
      {} as Record<string, unknown> & { mode?: { key: ModeKey; value: unknown } },
    );

    const exportParams = {
      Filters: [
        { Key: 'ChartLayoutId', Value: chartDataInfoExportProps.value.chartLayoutId! },
        { Key: 'ChartSetId', Value: chartDataInfoExportProps.value.chartSetId! },
        { Key: 'ChartId', Value: chartDataInfoExportProps.value.chartId! },
        { Key: 'Impl_Type', Value: chartDataInfoExportProps.value.implType! },
        { Key: 'Impl_Key_Ref', Value: chartDataInfoExportProps.value.implKeyRef! },
        ...['startDate', 'endDate', 'source', 'dynamicFilter'].map((key) => ({
          Key: key.charAt(0) + key.slice(1),
          Value: filterMap[key] || [],
        })),
        ...(filterMap.mode ? [{ Key: filterMap.mode.key, Value: filterMap.mode.value }] : []),
      ],
    };

    const queryString = new URLSearchParams({
      Report_Key: 'DataInfo_Report',
      Tracking_Mode: 'Tracking',
      File_Type: 'Csv',
      File_Exp: '1800',
      Filters: JSON.stringify(exportParams.Filters),

      // เพิ่มพารามิเตอร์ Activity Log
      logModule: MODULES.BI.toString(),
      logEvent: EVENTS.DOWNLOAD.toString(),
      logDetailEn: 'Exported Data Info Report',
      logDetailTh: 'ส่งออกข้อมูล Data Info',
    }).toString();

    window.open(`/export/report-preview?${queryString}`, '_blank');
  };

  onMounted(() => {
    columns.value = props.dataInfo.columns;
    data.value = props.dataInfo.data;
    pagination.totalRows = data.value.length;
  });

  return {
    columns,
    data,
    pagination,
    onCloseModal,
    renderSlotName,
    resolveComponent,
    resolveInfoFilterKey,
    resolveInfoFilterValue,
    onClickExportDataInfo,
    loadingExport,
  };
}
